import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  asignarSocioACaja,
  actualizarCaja,
  actualizarSocioCaja,
  actualizarTamano,
  crearCaja,
  crearTamano,
  crearPersonaCaja,
  actualizarPersonaCaja,
  eliminarPersonaCaja,
  desasignarSocioCaja,
  eliminarCaja,
  eliminarTamano,
  obtenerCajas,
  obtenerCofresVencidos,
  obtenerPersonasDeCaja,
  obtenerSociosDeCaja,
  obtenerTamanos,
} from "../../services/cajaService.tsx";
import {
  Caja,
  CajaPersona,
  CondicionSocio,
  SocioCaja,
  TamanoCaja,
  TipoPersonaCaja,
} from "../../types/caja.tsx";
import {
  Building2,
  CircleHelp,
  Download,
  FileSignature,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import socioService from "../../services/socioService.tsx";
import ContratosService from "../../services/contratosCofresService.tsx";
import logoCamara from "../../assets/LogoCamara.png";

type FiltroCajas = {
  estado?: string;
  tamanoId?: number;
  ubicacion?: string;
  condicionSocio?: CondicionSocio;
};

const estados = [
  "disponible",
  "ocupada",
  "mantenimiento",
  "bloqueada",
];

const colorPorTamano = (tamanoNombre?: string) => {
  const nombre = (tamanoNombre || "").toLowerCase();
  if (nombre.includes("peque")) return "border-blue-400 bg-blue-100/80";
  if (nombre.includes("mediana") || nombre.includes("medio"))
    return "border-emerald-400 bg-emerald-100/80";
  if (nombre.includes("grande")) return "border-purple-400 bg-purple-100/80";
  return "border-slate-300 bg-slate-50";
};

const colorPorCondicion = (condicion?: CondicionSocio) => {
  switch (condicion) {
    case "activo":
      return "bg-green-100 text-green-800 border-green-200";
    case "baja":
      return "bg-yellow-100 text-yellow-800 text-yellow-800 border-yellow-200";
    case "sin_socio":
      return "bg-red-100 text-red-800 border-red-200";
    case "legacy":
      return "bg-gray-100 text-gray-600 border-gray-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

const labelPorCondicion = (condicion?: CondicionSocio) => {
  switch (condicion) {
    case "activo":
      return "Socio activo";
    case "baja":
      return "Socio baja";
    case "sin_socio":
      return "Sin socio";
    case "legacy":
      return "Legacy";
    default:
      return "—";
  }
};

const BadgeCondicionSocio: React.FC<{ condicion?: CondicionSocio }> = ({ condicion }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${colorPorCondicion(condicion)}`}>
    {labelPorCondicion(condicion)}
  </span>
);

const obtenerMailSocio = (s: any) =>
  s?.socioMail ??
  s?.socio_mail ??
  s?.email ??
  s?.mail ??
  "";

const DOMICILIO_TOOLTIP =
  "Es el medio por el cual se le va a informar de cualquier cosa importante.";

const DomicilioElectronicoBadge: React.FC<{ value: string }> = ({ value }) => (
  <p className="group mt-1 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-800">
    Domicilio electrónico: {value}
    <span className="relative inline-flex items-center">
      <CircleHelp className="h-3.5 w-3.5 cursor-help text-blue-600" />
      <span className="pointer-events-none absolute left-1/2 top-[calc(100%+0.4rem)] z-20 w-64 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-center text-[11px] normal-case tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {DOMICILIO_TOOLTIP}
      </span>
    </span>
  </p>
);

const descargarArchivo = (contenido: string, nombre: string, mime = "text/html") => {
  const blob = new Blob([contenido], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const contratoHtml = (params: {
  cajaNumero?: string | number;
  tamanoNombre?: string;
  fecha?: string;
  firmaDigital?: string;
  titular?: {
    socioNombre?: string;
    socioId?: string | number;
    socioDni?: string;
    emailOficial?: string;
  };
  cotitulares: Array<{
    socioNombre?: string;
    socioId?: string | number;
    socioDni?: string;
    emailOficial?: string;
  }>;
}) => {
  const fechaBase = params.fecha ? new Date(params.fecha) : new Date();
  const dia = String(fechaBase.getDate()).padStart(2, "0");
  const mes = fechaBase.toLocaleDateString("es-AR", { month: "long" });
  const anio = fechaBase.getFullYear();
  const titularNombre = params.titular?.socioNombre || "No informado";
  const titularDni = params.titular?.socioDni || "No informado";
  const titularDomicilioElectronico = params.titular?.emailOficial || "No informado";
  const logoSrc = logoCamara.startsWith("http")
    ? logoCamara
    : `${window.location.origin}${logoCamara}`;
  const cotitularesTexto = params.cotitulares.length
    ? params.cotitulares
        .map(
          (c) =>
            `${c.socioNombre || "No informado"}, DNI ${c.socioDni || "No informado"}, domicilio electrónico ${c.emailOficial || "No informado"}`
        )
        .join("; ")
    : "No aplica";

  return `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Contrato de Alquiler de Caja de Seguridad</title>
  <style>
    :root {
      --ink: #0f172a;
      --muted: #475569;
      --line: #cbd5e1;
      --brand: #1e3a8a;
      --brand-soft: #dbeafe;
      --paper: #ffffff;
      --bg: #f1f5f9;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 26px;
      background: radial-gradient(circle at 20% 10%, #e2e8f0 0%, var(--bg) 45%, #eef2ff 100%);
      color: var(--ink);
      font-family: "Times New Roman", serif;
      line-height: 1.65;
    }
    .sheet {
      max-width: 980px;
      margin: 0 auto;
      background: var(--paper);
      border: 1px solid var(--line);
      border-radius: 14px;
      box-shadow: 0 20px 45px rgba(15, 23, 42, 0.14);
      padding: 30px 38px;
    }
    .header {
      display: grid;
      grid-template-columns: 96px 1fr;
      gap: 16px;
      align-items: center;
      border-bottom: 2px solid var(--brand);
      padding-bottom: 14px;
      margin-bottom: 18px;
      background: linear-gradient(90deg, var(--brand-soft), #ffffff 68%);
      border-radius: 10px;
      padding: 14px;
    }
    .logo {
      width: 84px;
      height: 84px;
      object-fit: contain;
      border-radius: 10px;
      background: #ffffff;
      border: 1px solid #bfdbfe;
      padding: 6px;
    }
    .kicker {
      margin: 0;
      color: var(--brand);
      font-size: 12px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      font-weight: 700;
    }
    h1 {
      margin: 4px 0 0;
      font-size: 24px;
      line-height: 1.25;
      text-transform: uppercase;
      color: #0b173b;
    }
    .meta {
      margin: 8px 0 0;
      color: var(--muted);
      font-size: 13px;
    }
    p { margin: 12px 0; font-size: 20px; }
    .block {
      margin-top: 14px;
      border: 1px solid var(--line);
      border-left: 4px solid var(--brand);
      border-radius: 8px;
      padding: 12px 14px;
      background: #f8fafc;
    }
    .firma {
      margin-top: 26px;
      border-top: 1px solid var(--line);
      padding-top: 12px;
    }
    .small { font-size: 12px; color: var(--muted); }
    @media print {
      body { background: #fff; padding: 0; }
      .sheet { box-shadow: none; border: none; border-radius: 0; max-width: none; }
    }
  </style>
</head>
<body>
  <main class="sheet">
    <header class="header">
      <img class="logo" src="${logoSrc}" alt="Logo Cámara Comercial de Bolívar" />
      <div>
        <p class="kicker">Cámara Comercial de Bolívar</p>
        <h1>Contrato de Alquiler y Prestación de Servicio de Caja de Seguridad</h1>
        <p class="meta">Documento generado por el sistema de gestión de cajas y contratos</p>
      </div>
    </header>

  <section class="block">
    <p>
    En la localidad de Bolívar, Provincia de Buenos Aires, a los ${dia} días del mes de ${mes} de ${anio},
    se celebra el presente contrato de alquiler y prestación de servicio de Caja de Seguridad entre la
    Cámara Comercial de Bolívar (en adelante “la Proveedora”), con domicilio en Av. General Paz 123, Bolívar,
    representada en este acto por Representante Institucional, en su carácter de Responsable Administrativo,
    por una parte; y por la otra, el Sr./Sra. ${titularNombre}, DNI ${titularDni}, con domicilio electrónico
    ${titularDomicilioElectronico} (en adelante “el Titular”), y de ser el caso, el/los
    Co-Titular/es ${cotitularesTexto}, quienes también suscriben y aceptan todas las cláusulas del presente contrato.
    La Proveedora se obliga a poner a disposición del Titular y/o Co-Titulares una Caja de Seguridad metálica
    tipo “${params.tamanoNombre || "Cofre de Mediana Capacidad"}” de medidas internas aproximadas según tamaño contratado, identificada
    en el sistema como Caja N° ${params.cajaNumero || "No informada"}, ubicada en las instalaciones propias de
    la Cámara Comercial de Bolívar, bajo las condiciones de uso, acceso y custodia establecidas en el presente contrato.
    </p>
  </section>

  <section class="block">
    <p>
    Las partes convienen que la Proveedora brindará en forma continua y durante el plazo del presente contrato
    el servicio de custodia y resguardo de bienes y documentos en la mencionada Caja de Seguridad, garantizando
    medidas razonables de seguridad física, acceso restringido y confidencialidad. El Titular y/o Co-Titulares
    reconocen haber verificado el estado y medidas de la Caja de Seguridad antes de la firma y aceptan que la
    responsabilidad de la Proveedora se limita al resguardo conforme a los estándares de la Cámara Comercial de
    Bolívar, eximiéndose de responsabilidad por pérdida o daño que no resulte de dolo o negligencia grave.
    El presente contrato tendrá vigencia por un plazo de un año, renovable automáticamente por períodos iguales,
    salvo notificación en contrario con un mínimo de 30 días de anticipación por escrito.
    </p>
  </section>

  <section class="block">
    <p>
    A los fines de comunicaciones formales de cortes de servicio y/o deudas, las partes acuerdan como
    domicilio electrónico oficial de notificación el/los domicilio/s electrónico/s consignado/s en este contrato.
    </p>
  </section>

  <div class="firma">
    <p><strong>Firma digital registrada:</strong> ${params.firmaDigital || "Pendiente"}</p>
    <p class="small">Documento ficticio de prueba generado por el sistema.</p>
  </div>
  </main>
</body>
</html>`;
};

const normalizarSocioAsignado = (s: any): SocioCaja => ({
  id: s.id ?? s.socio_caja_id,
  socioId: s.socioId ?? s.socio_id,
  socioNombre: s.socioNombre ?? s.socio_nombre ?? s.nombre,
  socioDni: (s as any).socio_dni ?? (s as any).dni,
  socioMail: obtenerMailSocio(s),
  esTitular:
    s.esTitular ??
    s.es_titular ??
    (s.SocioCajaSeguridad?.es_titular ?? s.socioCajaSeguridad?.es_titular) ??
    true,
  fechaInicio:
    s.fechaInicio ??
    s.fecha_inicio ??
    s.SocioCajaSeguridad?.fecha_inicio ??
    s.socioCajaSeguridad?.fecha_inicio,
  fechaFin:
    s.fechaFin ??
    s.fecha_fin ??
    s.SocioCajaSeguridad?.fecha_fin ??
    s.socioCajaSeguridad?.fecha_fin,
  nota: s.nota ?? s.SocioCajaSeguridad?.nota ?? s.socioCajaSeguridad?.nota,
});

const TIPOS_DOCUMENTO = ["DNI", "CUIT", "CUIL", "Pasaporte", "LC", "LE"];

const TITULAR_MAX = 2;
const AUTORIZADO_MAX = 3;

const personaVacia = (tipo: TipoPersonaCaja): CajaPersona => ({
  tipo,
  nombre: "",
  tipoDocumento: "DNI",
  numeroDocumento: "",
  cuit: "",
  calle: "",
  numero: "",
  piso: "",
  depto: "",
  cp: "",
  ciudad: "",
  provincia: "",
  telefono: "",
  celular: "",
  email: "",
  idTarjeta: "",
});

const normalizarPersona = (p: any): CajaPersona => ({
  id: p?.id ?? p?.caja_persona_id,
  cajaId: p?.cajaId ?? p?.caja_id,
  tipo: (p?.tipo === "autorizado" ? "autorizado" : "titular") as TipoPersonaCaja,
  nombre: p?.nombre ?? "",
  tipoDocumento: p?.tipoDocumento ?? p?.tipo_documento ?? "",
  numeroDocumento: p?.numeroDocumento ?? p?.numero_documento ?? "",
  cuit: p?.cuit ?? "",
  calle: p?.calle ?? "",
  numero: p?.numero ?? "",
  piso: p?.piso ?? "",
  depto: p?.depto ?? "",
  cp: p?.cp ?? "",
  ciudad: p?.ciudad ?? "",
  provincia: p?.provincia ?? "",
  telefono: p?.telefono ?? "",
  celular: p?.celular ?? "",
  email: p?.email ?? "",
  idTarjeta: p?.idTarjeta ?? p?.id_tarjeta ?? "",
  deleted: p?.deleted,
});

const personaPayload = (p: CajaPersona) => ({
  tipo: p.tipo,
  nombre: p.nombre.trim(),
  tipo_documento: p.tipoDocumento?.trim() || null,
  numero_documento: p.numeroDocumento?.trim() || null,
  cuit: p.cuit.trim(),
  calle: p.calle?.trim() || null,
  numero: p.numero?.trim() || null,
  piso: p.piso?.trim() || null,
  depto: p.depto?.trim() || null,
  cp: p.cp?.trim() || null,
  ciudad: p.ciudad?.trim() || null,
  provincia: p.provincia?.trim() || null,
  telefono: p.telefono?.trim() || null,
  celular: p.celular?.trim() || null,
  email: p.email?.trim() || null,
  id_tarjeta: p.idTarjeta?.trim() || null,
});

const CajaPersonaEditor: React.FC<{
  persona: CajaPersona;
  index: number;
  onChange: (patch: Partial<CajaPersona>) => void;
  onRemove?: () => void;
  disabled?: boolean;
}> = ({ persona, index, onChange, onRemove, disabled }) => {
  const input =
    "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none disabled:bg-gray-50";
  const esTitular = persona.tipo === "titular";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">
          {esTitular ? "Titular" : "Autorizado"} #{index + 1}
        </p>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Quitar
          </button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
          Nombre *
          <input
            value={persona.nombre}
            onChange={(e) => onChange({ nombre: e.target.value })}
            disabled={disabled}
            className={input}
            placeholder="Nombre y apellido"
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          C.U.I.T *
          <input
            value={persona.cuit}
            onChange={(e) => onChange({ cuit: e.target.value })}
            disabled={disabled}
            className={input}
            placeholder="20-12345678-9"
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Tipo de documento
          <select
            value={persona.tipoDocumento ?? ""}
            onChange={(e) => onChange({ tipoDocumento: e.target.value })}
            disabled={disabled}
            className={input}
          >
            <option value="">Sin especificar</option>
            {TIPOS_DOCUMENTO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Número de documento
          <input
            value={persona.numeroDocumento ?? ""}
            onChange={(e) => onChange({ numeroDocumento: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          ID Tarjeta
          <input
            value={persona.idTarjeta ?? ""}
            onChange={(e) => onChange({ idTarjeta: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Calle
          <input
            value={persona.calle ?? ""}
            onChange={(e) => onChange({ calle: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Número (domicilio)
          <input
            value={persona.numero ?? ""}
            onChange={(e) => onChange({ numero: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Piso
          <input
            value={persona.piso ?? ""}
            onChange={(e) => onChange({ piso: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Depto.
          <input
            value={persona.depto ?? ""}
            onChange={(e) => onChange({ depto: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          C.P.
          <input
            value={persona.cp ?? ""}
            onChange={(e) => onChange({ cp: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Ciudad
          <input
            value={persona.ciudad ?? ""}
            onChange={(e) => onChange({ ciudad: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Provincia
          <input
            value={persona.provincia ?? ""}
            onChange={(e) => onChange({ provincia: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Teléfono
          <input
            value={persona.telefono ?? ""}
            onChange={(e) => onChange({ telefono: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600">
          Celular
          <input
            value={persona.celular ?? ""}
            onChange={(e) => onChange({ celular: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
        <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
          E-mail
          <input
            type="email"
            value={persona.email ?? ""}
            onChange={(e) => onChange({ email: e.target.value })}
            disabled={disabled}
            className={input}
          />
        </label>
      </div>
    </div>
  );
};

const COFRE_LETRA_ID_MAP: Record<string, string> = {
  "0": "A",
  "1": "B",
  "2": "C",
  "3": "D",
  "4": "E",
  "5": "F",
  "6": "G",
  "7": "H",
  "8": "I",
  "9": "J",
  "24": "X",
  "26": "Z",
};

const resolverLetraCofre = (valor: any) => {
  const raw = String(valor ?? "").trim().toUpperCase();
  if (!raw) return "";
  if (/^[A-ZÑ]$/.test(raw)) return raw;
  return COFRE_LETRA_ID_MAP[raw] || "";
};

const codigoCajaDe = (caja: any): string => {
  const candidato = caja?.codigoCaja;
  if (candidato) return String(candidato);

  const rawNumero = String(caja?.numero ?? "").trim().toUpperCase();
  const codigoDirecto = rawNumero.match(/^([A-Z])\s*[-/]?\s*(\d+)$/);
  if (codigoDirecto) return `${codigoDirecto[1]}-${codigoDirecto[2]}`;

  const letra =
    resolverLetraCofre(caja?.cofreLetra) || resolverLetraCofre(caja?.numero);
  const match = rawNumero.match(/\d+/);
  if (letra && match) return `${letra}-${match[0]}`;
  if (letra) return letra;
  return rawNumero;
};

const CajasPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [msg, setMsg] = useState<string | null>(null);
  const [msgTipo, setMsgTipo] = useState<"ok" | "warn" | "error" | null>(null);
  const [tamanos, setTamanos] = useState<TamanoCaja[]>([]);
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState<FiltroCajas>({});
  const [busquedaCaja, setBusquedaCaja] = useState("");
  const [sortBy, setSortBy] = useState<"numero" | "estado" | "tamano" | "socio" | "ubicacion">("numero");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [tab, setTab] = useState<"cajas" | "tamanos">("cajas");
  const [cofresVencidos, setCofresVencidos] = useState<any[]>([]);
  const [loadingVencidos, setLoadingVencidos] = useState(false);
  const [mostrarVencidos, setMostrarVencidos] = useState(false);
  const [soloVencidos, setSoloVencidos] = useState(true);
  const [seleccionCaja, setSeleccionCaja] = useState<Caja | null>(null);
  const [sociosDeCaja, setSociosDeCaja] = useState<SocioCaja[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostrarCrearAsignar, setMostrarCrearAsignar] = useState(false);
  const [nuevoTamanoId, setNuevoTamanoId] = useState<number | undefined>();
  const [nuevoSocioId, setNuevoSocioId] = useState("");
  const [nuevoSocioSeleccionado, setNuevoSocioSeleccionado] = useState<any | null>(null);
  const [busquedaSocio, setBusquedaSocio] = useState("");
  const [resultadosSocio, setResultadosSocio] = useState<any[]>([]);
  const [buscandoSocio, setBuscandoSocio] = useState(false);
  const [nuevaUbicacion, setNuevaUbicacion] = useState("");
  const [nuevoCofreLetra, setNuevoCofreLetra] = useState("");
  const [nuevoCofreNumero, setNuevoCofreNumero] = useState("");
  const [nuevosTitulares, setNuevosTitulares] = useState<CajaPersona[]>([]);
  const [nuevosAutorizados, setNuevosAutorizados] = useState<CajaPersona[]>([]);
  const [confirmEliminar, setConfirmEliminar] = useState<{ id: number; numero?: string } | null>(null);
  const [editarTamano, setEditarTamano] = useState<TamanoCaja | null>(null);

  const resumenSocios = (socios?: any[]) => {
    const nombres = (socios || [])
      .map((s) => s?.socio_nombre || s?.socioNombre)
      .filter(Boolean) as string[];
    if (!nombres.length) return "—";
    const extra = nombres.length - 1;
    return extra > 0 ? `${nombres[0]} y ${extra} más` : nombres[0];
  };
  const [editarTamanoForm, setEditarTamanoForm] = useState<{
    nombre: string;
    descripcion: string;
    precioMensual: string | number;
    precioAnual: string | number;
  }>({ nombre: "", descripcion: "", precioMensual: "", precioAnual: "" });
  const [guardandoTamano, setGuardandoTamano] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        setError(null);
        const token = await getAccessTokenSilently();
        const tamanosApi = await obtenerTamanos(token);
        const normalizados = (tamanosApi || []).map((t: any) => ({
          id: t.id ?? t.tamano_id,
          nombre: t.nombre,
          descripcion: t.descripcion,
          precioMensual: t.precioMensual ?? t.precio_mensual,
          precioAnual: t.precioAnual ?? t.precio_anual,
          active: t.active ?? t.activo,
        }));
        setTamanos(normalizados);
        const data = await obtenerCajas({ ...filtros }, token);
        const items = Array.isArray((data as any)?.items)
          ? (data as any).items
          : Array.isArray(data)
          ? (data as any)
          : [];
        const normalizadosCajas = items.map((c: any) => {
          const sociosNorm = Array.isArray(c.socios)
            ? c.socios.map(normalizarSocioAsignado)
            : [];
          return {
            ...c,
            id: c.id ?? c.caja_id,
            tamanoId: Number(c.tamanoId ?? c.tamano_id ?? 0),
            titularNombre:
              sociosNorm[0]?.socioNombre,
              socios: sociosNorm,
            personas: Array.isArray(c.personas)
              ? c.personas.map(normalizarPersona)
              : [],
          };
        });
        setCajas(normalizadosCajas);
      } catch (err: any) {
        setError("No se pudieron cargar las cajas o tamaños.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [JSON.stringify(filtros), getAccessTokenSilently]);

  useEffect(() => {
    const overlayAbierto =
      mostrarCrearAsignar || !!seleccionCaja || !!confirmEliminar || !!editarTamano;
    if (!overlayAbierto) return;
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflowPrevio;
    };
  }, [mostrarCrearAsignar, seleccionCaja, confirmEliminar, editarTamano]);

  const tamanosLookup = useMemo(() => {
    const map = new Map<number, TamanoCaja>();
    tamanos.forEach((t) => map.set(t.id, t));
    return map;
  }, [tamanos]);

  const cajasFiltradas = useMemo(() => {
    let resultado = cajas;

    // Filtros del dropdown
    if (filtros.estado) {
      resultado = resultado.filter((c) => c.estado === filtros.estado);
    }
    if (filtros.tamanoId) {
      resultado = resultado.filter((c) => c.tamanoId === filtros.tamanoId);
    }
    if (filtros.ubicacion) {
      const ubic = filtros.ubicacion.toLowerCase();
      resultado = resultado.filter((c) => String(c.ubicacion || "").toLowerCase().includes(ubic));
    }
    if (filtros.condicionSocio) {
      resultado = resultado.filter((c) => c.condicionSocio === filtros.condicionSocio);
    }

    // Búsqueda por texto
    const q = busquedaCaja.trim().toLowerCase();
    if (!q) return resultado;

    return resultado.filter((caja) => {
      const numero = [String(caja.numero || ""), codigoCajaDe(caja)].join(" ").toLowerCase();
      const ubicacion = String(caja.ubicacion || "").toLowerCase();
      const estado = String(caja.estado || "").toLowerCase();
      const condicion = String(caja.condicionSocio || "").toLowerCase();
      const socios = (caja.socios || []) as any[];
      const sociosTexto = socios
        .map((s) => `${s?.socioNombre || ""} ${s?.socioId || ""} ${s?.socioDni || ""}`.toLowerCase())
        .join(" ");

      return (
        numero.includes(q) ||
        ubicacion.includes(q) ||
        estado.includes(q) ||
        condicion.includes(q) ||
        sociosTexto.includes(q)
      );
    });
  }, [cajas, busquedaCaja, filtros]);

  const cajasOrdenadas = useMemo(() => {
    const getSocio = (caja: any) => {
      const resumen = resumenSocios(caja.socios);
      return (resumen !== "—" ? resumen : caja.titularNombre || "").toString();
    };

    const sorted = [...cajasFiltradas].sort((a: any, b: any) => {
      let av: any = "";
      let bv: any = "";

      if (sortBy === "numero") {
        av = codigoCajaDe(a);
        bv = codigoCajaDe(b);
      } else if (sortBy === "estado") {
        av = String(a.estado || "");
        bv = String(b.estado || "");
      } else if (sortBy === "tamano") {
        av = String(tamanosLookup.get(a.tamanoId)?.nombre || "");
        bv = String(tamanosLookup.get(b.tamanoId)?.nombre || "");
      } else if (sortBy === "socio") {
        av = getSocio(a);
        bv = getSocio(b);
      } else if (sortBy === "ubicacion") {
        av = String(a.ubicacion || "");
        bv = String(b.ubicacion || "");
      }

      const cmp = String(av).localeCompare(String(bv), "es", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [cajasFiltradas, sortBy, sortDir, tamanosLookup]);

  const PAGE_SIZE = 20;
  const totalPaginas = Math.max(1, Math.ceil(cajasOrdenadas.length / PAGE_SIZE));
  const cajasPaginadas = useMemo(() => {
    const start = (pagina - 1) * PAGE_SIZE;
    return cajasOrdenadas.slice(start, start + PAGE_SIZE);
  }, [cajasOrdenadas, pagina]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(1);
  }, [pagina, totalPaginas]);

  const toggleSort = (col: "numero" | "estado" | "tamano" | "socio" | "ubicacion") => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPagina(1);
  };

  const sortIndicator = (col: "numero" | "estado" | "tamano" | "socio" | "ubicacion") =>
    sortBy === col ? (sortDir === "asc" ? "↑" : "↓") : "↕";

  const cargarVencidos = async () => {
    try {
      setLoadingVencidos(true);
      const token = await getAccessTokenSilently();
      const data = await obtenerCofresVencidos(token);
      setCofresVencidos(data || []);
      setMostrarVencidos(true);
    } catch (err) {
      console.error("No se pudieron cargar cofres vencidos", err);
      setCofresVencidos([]);
      setMostrarVencidos(true);
    } finally {
      setLoadingVencidos(false);
    }
  };

  const vencidosFiltrados = useMemo(() => {
    if (!soloVencidos) return cofresVencidos;
    return (cofresVencidos || []).filter((r: any) => Number(r.vencido) === 1);
  }, [cofresVencidos, soloVencidos]);

  const abrirDetalle = async (caja: Caja) => {
    try {
      setSeleccionCaja(caja);
      const sociosLocales = Array.isArray(caja.socios)
        ? caja.socios.map(normalizarSocioAsignado)
        : [];
      setSociosDeCaja(sociosLocales);

      const isLegacy = (caja as any).origen === "legacy" || Boolean((caja as any).legacyContratoId) || Number(caja.id) < 0;
      if (isLegacy) return;

      const token = await getAccessTokenSilently();
      const sociosRemotos = await obtenerSociosDeCaja(Number(caja.id), token);
      const normalizados = Array.isArray(sociosRemotos)
        ? sociosRemotos.map(normalizarSocioAsignado)
        : [];
      setSociosDeCaja(normalizados);
      setCajas((prev) =>
        prev.map((c) =>
          c.id === caja.id
            ? {
                ...c,
                socios: normalizados,
                titularNombre: normalizados[0]?.socioNombre || c.titularNombre,
              }
            : c
        )
      );
    } catch (err) {
      console.error("No se pudo cargar el detalle de la caja", err);
    }
  };

  const onCrearTamano = async (payload: Partial<TamanoCaja>) => {
    const token = await getAccessTokenSilently();
    const nuevo = await crearTamano(payload, token);
    setTamanos((prev) => [...prev, nuevo]);
  };

  const onActualizarTamano = async (id: number, payload: Partial<TamanoCaja>) => {
    const token = await getAccessTokenSilently();
    const actualizado = await actualizarTamano(id, payload, token);
    // Mezclamos payload por si la API no devuelve todos los campos que editamos.
    setTamanos((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              ...payload,
              ...actualizado,
            }
          : t
      )
    );
  };

  const onEliminarTamano = async (id: number) => {
    const token = await getAccessTokenSilently();
    await eliminarTamano(id, token);
    setTamanos((prev) => prev.filter((t) => t.id !== id));
  };

  const abrirEditarTamano = (t: TamanoCaja) => {
    setEditarTamano(t);
    setEditarTamanoForm({
      nombre: t.nombre || "",
      descripcion: t.descripcion || "",
      precioMensual: t.precioMensual ?? "",
      precioAnual: t.precioAnual ?? "",
    });
  };

  const guardarEdicionTamano = async () => {
    if (!editarTamano) return;
    const precioMensualNum =
      editarTamanoForm.precioMensual === "" ? undefined : Number(editarTamanoForm.precioMensual);
    const precioAnualNum =
      editarTamanoForm.precioAnual === "" ? undefined : Number(editarTamanoForm.precioAnual);

    if (
      (typeof precioMensualNum === "number" && precioMensualNum < 0) ||
      (typeof precioAnualNum === "number" && precioAnualNum < 0)
    ) {
      alert("Los precios no pueden ser menores que 0.");
      return;
    }

    try {
      setGuardandoTamano(true);
      await onActualizarTamano(editarTamano.id, {
        nombre: editarTamanoForm.nombre.trim() || editarTamano.nombre,
        descripcion: editarTamanoForm.descripcion.trim() || undefined,
        precioMensual: precioMensualNum,
        precioAnual: precioAnualNum,
      });
      setEditarTamano(null);
    } catch (err) {
      console.error("No se pudo actualizar el tamaño", err);
      alert("No se pudo actualizar el tamaño.");
    } finally {
      setGuardandoTamano(false);
    }
  };

  const onCrearCaja = async (payload: Partial<Caja>) => {
    const token = await getAccessTokenSilently();
    const creada = await crearCaja(payload, token);
    const sociosNorm = Array.isArray((creada as any).socios)
      ? (creada as any).socios.map(normalizarSocioAsignado)
      : [];
    setCajas((prev) => [
      {
        ...creada,
        id: (creada as any).id ?? (creada as any).caja_id,
        tamanoId: Number((creada as any).tamanoId ?? (creada as any).tamano_id ?? 0),
        socios: sociosNorm,
        titularNombre: sociosNorm[0]?.socioNombre,
      },
      ...prev,
    ]);
  };

  const onEliminarCaja = async (id: number) => {
    const token = await getAccessTokenSilently();
    await eliminarCaja(id, token);
    setCajas((prev) => prev.filter((c) => c.id !== id));
    if (seleccionCaja?.id === id) {
      setSeleccionCaja(null);
      setSociosDeCaja([]);
    }
  };

  const onAsignarSocio = async (
    socioId: string | number,
    payload: Partial<SocioCaja>
  ) => {
    if (!seleccionCaja) return;
    const token = await getAccessTokenSilently();
    const socioIdNum = Number(socioId);
    const asignado = await asignarSocioACaja(
      { socioId: socioIdNum, cajaId: Number(seleccionCaja.id), ...payload },
      token
    );
    const normalizado = normalizarSocioAsignado({
      socioId: socioIdNum,
      ...asignado,
      socioNombre:
        asignado.socioNombre ??
        asignado.socio_nombre ??
        (payload as any)?.socioNombre,
      socio_dni: asignado.socio_dni ?? (payload as any)?.socioDni,
    });
    setSociosDeCaja((prev) => {
      const existe = prev.some((s) => String(s.socioId) === String(normalizado.socioId));
      return existe ? prev : [...prev, normalizado];
    });
    setCajas((prev) =>
      prev.map((c) => {
        if (c.id !== seleccionCaja.id) return c;
        const sociosActualizados = Array.isArray(c.socios)
          ? [...c.socios, normalizado].filter(
              (s, i, arr) =>
                arr.findIndex((x) => String((x as any).socioId) === String((s as any).socioId)) === i
            )
          : [normalizado];
        return {
          ...c,
          socios: sociosActualizados,
          titularNombre: c.titularNombre || normalizado.socioNombre,
        };
      })
    );
    setMsg("Socio asociado. El contrato único de la caja se actualiza al descargar o firmar.");
    setMsgTipo("ok");
    return normalizado;
  };

  const onDesasignarSocio = async (cajaId: number, socioId: number) => {
    if (!seleccionCaja) return;
    const token = await getAccessTokenSilently();
    await desasignarSocioCaja(cajaId, socioId, token);
    setSociosDeCaja((prev) =>
      prev.filter(
        (s) => s.id !== socioId && Number(s.socioId) !== Number(socioId)
      )
    );
    setCajas((prev) =>
      prev.map((c) => {
        if (c.id !== cajaId) return c;
        const sociosActualizados = (c.socios || []).filter(
          (s) => s.id !== socioId && Number((s as any).socioId) !== Number(socioId)
        );
        return {
          ...c,
          socios: sociosActualizados,
          titularNombre: sociosActualizados[0]?.socioNombre,
        };
      })
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400">
            Infraestructura
          </p>
          <h1 className="text-3xl font-semibold text-gray-900">
            Gestión de Cajas de Seguridad
          </h1>
          <p className="text-gray-500">
            Administra cajas, tamaños y asignaciones a socios.
          </p>
        </div>
        <div className="inline-flex rounded-lg bg-gray-100 p-1 text-sm">
          <button
            className={`px-4 py-2 rounded-md ${
              tab === "cajas" ? "bg-white shadow font-semibold" : ""
            }`}
            onClick={() => setTab("cajas")}
          >
            Cajas
          </button>
          <button
            className={`px-4 py-2 rounded-md ${
              tab === "tamanos" ? "bg-white shadow font-semibold" : ""
            }`}
            onClick={() => setTab("tamanos")}
          >
            Tamaños
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {msg && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            msgTipo === "error"
              ? "bg-red-50 text-red-700"
              : msgTipo === "warn"
              ? "bg-amber-50 text-amber-700"
              : "bg-emerald-50 text-emerald-700"
          }`}
        >
          {msg}
        </div>
      )}

      {confirmEliminar && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900">
              Eliminar caja {confirmEliminar.numero || confirmEliminar.id}?
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Esta acción no se puede deshacer. Se eliminará la caja y sus asignaciones.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmEliminar(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  onEliminarCaja(confirmEliminar.id);
                  setConfirmEliminar(null);
                }}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "cajas" ? (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={filtros.estado ?? ""}
                onChange={(e) => {
                  setFiltros((f) => ({
                    ...f,
                    estado: e.target.value || undefined,
                  }));
                  setPagina(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">Estado</option>
                {estados.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
              <select
                value={filtros.tamanoId ?? ""}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  setFiltros((f) => ({
                    ...f,
                    tamanoId: val,
                  }));
                  setPagina(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">Tamaño</option>
                {tamanos.map((t) => {
                  const tid = (t as any).id ?? (t as any).tamano_id;
                  return (
                    <option key={tid} value={Number(tid)}>
                      {t.nombre}
                    </option>
                  );
                })}
              </select>
              <input
                placeholder="Ubicación"
                value={filtros.ubicacion ?? ""}
                onChange={(e) => {
                  setFiltros((f) => ({
                    ...f,
                    ubicacion: e.target.value || undefined,
                  }));
                  setPagina(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
              <select
                value={filtros.condicionSocio ?? ""}
                onChange={(e) => {
                  setFiltros((f) => ({
                    ...f,
                    condicionSocio: (e.target.value as CondicionSocio) || undefined,
                  }));
                  setPagina(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">Condición socio</option>
                <option value="activo">Socio activo</option>
                <option value="baja">Socio baja</option>
                <option value="sin_socio">Sin socio</option>
                <option value="legacy">Legacy</option>
              </select>
              <input
                placeholder="Buscar por socio, DNI, ID, caja..."
                value={busquedaCaja}
                onChange={(e) => {
                  setBusquedaCaja(e.target.value);
                  setPagina(1);
                }}
                className="min-w-[260px] rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={() => setMostrarCrearAsignar(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
              <Plus className="h-4 w-4" />
              Crear y asignar
            </button>
            <button
              onClick={cargarVencidos}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 shadow-sm transition hover:bg-amber-100"
            >
              Ver vencidos
            </button>
          </div>

          <p className="text-xs text-slate-500">
            Mostrando {cajasFiltradas.length} de {cajas.length} cajas
            {busquedaCaja.trim() ? ` · filtro: "${busquedaCaja.trim()}"` : ""}
          </p>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left"><button className="font-semibold" onClick={() => toggleSort("numero")}>Caja {sortIndicator("numero")}</button></th>
                  <th className="px-3 py-2 text-left"><button className="font-semibold" onClick={() => toggleSort("estado")}>Estado {sortIndicator("estado")}</button></th>
                  <th className="px-3 py-2 text-left"><button className="font-semibold" onClick={() => toggleSort("tamano")}>Tamaño {sortIndicator("tamano")}</button></th>
                  <th className="px-3 py-2 text-left"><button className="font-semibold" onClick={() => toggleSort("socio")}>Socio {sortIndicator("socio")}</button></th>
                  <th className="px-3 py-2 text-left">Condición</th>
                  <th className="px-3 py-2 text-left"><button className="font-semibold" onClick={() => toggleSort("ubicacion")}>Ubicación {sortIndicator("ubicacion")}</button></th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Cargando cajas...</td></tr>
                ) : cajasPaginadas.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No hay cajas que coincidan con ese filtro.</td></tr>
                ) : (
                  cajasPaginadas.map((caja) => (
                    <tr key={caja.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-900">{codigoCajaDe(caja)}</td>
                      <td className="px-3 py-2">
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">{caja.estado || "—"}</span>
                      </td>
                      <td className="px-3 py-2">{tamanosLookup.get(caja.tamanoId)?.nombre || "—"}</td>
                      <td className="px-3 py-2">{(() => { const resumen = resumenSocios(caja.socios); return resumen !== "—" ? resumen : caja.titularNombre || "—"; })()}</td>
                      <td className="px-3 py-2"><BadgeCondicionSocio condicion={caja.condicionSocio} /></td>
                      <td className="px-3 py-2">{caja.ubicacion || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button onClick={() => abrirDetalle(caja)} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white">Ver</button>
                          {(caja as any).origen !== "legacy" && !(caja as any).legacyContratoId && (
                            <button onClick={() => setConfirmEliminar({ id: caja.id, numero: codigoCajaDe(caja) })} className="rounded-lg border border-red-100 bg-red-50 p-1.5 text-red-600" aria-label="Eliminar caja"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Página {pagina} de {totalPaginas}</span>
            <div className="inline-flex items-center gap-2">
              <button
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
              >Anterior</button>
              <button
                disabled={pagina >= totalPaginas}
                onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
              >Siguiente</button>
            </div>
          </div>

          {mostrarVencidos && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-amber-900">Cofres vencidos (endpoint)</p>
                <button className="text-xs text-amber-800 underline" onClick={() => setMostrarVencidos(false)}>Ocultar</button>
              </div>
              {loadingVencidos ? (
                <p className="text-sm text-amber-800">Cargando vencidos...</p>
              ) : cofresVencidos.length === 0 ? (
                <p className="text-sm text-amber-800">No se encontraron vencidos para socios con más de un contrato.</p>
              ) : (
                <>
                  <label className="mb-2 inline-flex items-center gap-2 text-xs text-amber-900">
                    <input
                      type="checkbox"
                      checked={soloVencidos}
                      onChange={(e) => setSoloVencidos(e.target.checked)}
                    />
                    Solo vencidos
                  </label>
                  <div className="max-h-64 overflow-auto rounded-lg border border-amber-200 bg-white">
                  <table className="min-w-full text-xs">
                    <thead className="bg-amber-50 text-amber-900">
                      <tr>
                        <th className="px-2 py-1 text-left">Socio ID</th>
                        <th className="px-2 py-1 text-left">Socio</th>
                        <th className="px-2 py-1 text-left">Contrato</th>
                        <th className="px-2 py-1 text-left">Vence</th>
                        <th className="px-2 py-1 text-left">Vencido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vencidosFiltrados.slice(0, 300).map((r: any, idx: number) => (
                        <tr key={`${r.socio_id}-${r.contrato_id}-${idx}`} className="border-t border-amber-100">
                          <td className="px-2 py-1">{r.socio_id}</td>
                          <td className="px-2 py-1">{r.socio_nombre}</td>
                          <td className="px-2 py-1">{r.contrato_id}</td>
                          <td className="px-2 py-1">{r.fecha_vencimiento || "—"}</td>
                          <td className="px-2 py-1">{Number(r.vencido) === 1 ? "Sí" : "No"}</td>
                        </tr>
                      ))}
                      {vencidosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-2 py-3 text-center text-amber-800">No hay filas para el filtro seleccionado.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          )}
        </section>
      ) : (
        <section className="space-y-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Tamaños</h2>
            <button
              onClick={() =>
                onCrearTamano({
                  nombre: `Tamaño ${tamanos.length + 1}`,
                  descripcion: "Nuevo tamaño",
                })
              }
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Nuevo tamaño rápido
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {tamanos.map((t) => (
              <div
                key={t.id}
                className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 shadow-inner"
              >
                <div className="flex items-center justify-between">
                  <p className="text-base font-semibold text-gray-900">
                    {t.nombre}
                  </p>
                  <button
                    onClick={() => abrirEditarTamano(t)}
                    className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-100"
                    aria-label="Editar tamaño"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  {t.descripcion || "Sin descripción"}
                </p>
                <p className="text-xs text-gray-500">
                  Mensual: {t.precioMensual ?? "—"} · Anual:{" "}
                  {t.precioAnual ?? "—"}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {seleccionCaja && (
        <CajaDetalleDrawer
          key={seleccionCaja.id}
          caja={seleccionCaja}
          onClose={() => {
            setSeleccionCaja(null);
            setSociosDeCaja([]);
          }}
          socios={sociosDeCaja}
          onDesasignar={onDesasignarSocio}
          onAsignar={onAsignarSocio}
          onEditarCaja={async (actualizada) => {
            const normalizada = {
              ...actualizada,
              id:
                (actualizada as any).id ??
                (actualizada as any).caja_id,
              tamanoId: Number(
                (actualizada as any).tamanoId ??
                  (actualizada as any).tamano_id ??
                  0
              ),
              codigoCaja: codigoCajaDe(actualizada),
            };
            setCajas((prev) =>
              prev.map((c) =>
                Number(c.id) === Number(normalizada.id)
                  ? { ...c, ...normalizada }
                  : c
              )
            );
            setSeleccionCaja((prev) =>
              prev && Number(prev.id) === Number(normalizada.id)
                ? { ...prev, ...normalizada }
                : prev
            );
            setMsg("Código de caja actualizado.");
            setMsgTipo("ok");
          }}
        />
      )}

      {mostrarCrearAsignar && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-900/60 px-0 py-0 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-2xl border border-slate-400 bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400">
                    Admin
                  </p>
                  <h3 className="text-2xl font-semibold text-gray-900">
                    Crear caja y asignar
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    El socio es obligatorio. Los titulares y autorizados son opcionales.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMostrarCrearAsignar(false);
                    setNuevoSocioSeleccionado(null);
                    setNuevoCofreLetra("");
                    setNuevoCofreNumero("");
                    setNuevosTitulares([]);
                    setNuevosAutorizados([]);
                  }}
                className="rounded-lg px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
              >
                Cerrar
              </button>
            </div>

            <div className="mt-4 grid gap-3">
              <label className="text-sm text-gray-700">
                Tamaño
                <select
                  value={nuevoTamanoId ?? ""}
                  onChange={(e) =>
                    setNuevoTamanoId(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                >
                  <option value="">Seleccioná un tamaño</option>
                  {tamanos.map((t) => {
                    const tid = (t as any).id ?? (t as any).tamano_id;
                    return (
                      <option key={tid} value={Number(tid)}>
                        {t.nombre}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                Buscar socio
                <div className="mt-1 flex gap-2">
                  <input
                    value={busquedaSocio}
                    onChange={(e) => setBusquedaSocio(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                    placeholder="Nombre o ID"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!busquedaSocio.trim()) return;
                      try {
                        setBuscandoSocio(true);
                        const data = await socioService.searchSocios(
                          busquedaSocio.trim(),
                          getAccessTokenSilently
                        );
                        setResultadosSocio(data || []);
                      } catch (err) {
                        console.error("Error buscando socio", err);
                        setResultadosSocio([]);
                      } finally {
                        setBuscandoSocio(false);
                      }
                    }}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                  >
                    Buscar
                  </button>
                </div>
                {buscandoSocio && (
                  <p className="mt-1 text-xs text-gray-500">Buscando...</p>
                )}
                {resultadosSocio.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                    {resultadosSocio.map((s) => (
                      <button
                        type="button"
                        key={s.socio_id}
                        onClick={() => {
                    setNuevoSocioId(String(s.socio_id));
                    setNuevoSocioSeleccionado(s);
                    setBusquedaSocio(`${s.socio_nombre} (${s.socio_id})`);
                    setResultadosSocio([]);
                  }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">
                            {s.socio_nombre}
                          </p>
                          <p className="text-xs text-gray-500">
                            ID: {s.socio_id} · DNI: {s.socio_dni || "—"}
                          </p>
                        </div>
                        <span className="text-xs text-blue-600">Seleccionar</span>
                      </button>
                    ))}
                  </div>
                )}
                {nuevoSocioId && (
                  <p className="mt-1 text-xs text-emerald-700">
                    Socio seleccionado: {busquedaSocio || nuevoSocioId}
                  </p>
                )}
              </label>
              <label className="text-sm text-gray-700">
                Ubicación (opcional)
                <input
                  value={nuevaUbicacion}
                  onChange={(e) => setNuevaUbicacion(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                  placeholder="Ej: Piso 1 - Box A"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-gray-700">
                  Letra (módulo)
                  <input
                    value={nuevoCofreLetra}
                    onChange={(e) => setNuevoCofreLetra(e.target.value.toUpperCase())}
                    list="letras-cofre"
                    maxLength={2}
                    placeholder="Ej: A"
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                  />
                  <datalist id="letras-cofre">
                    {["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"].map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </datalist>
                </label>
                <label className="text-sm text-gray-700">
                  Número (opcional, auto si se deja vacío)
                  <input
                    value={nuevoCofreNumero}
                    onChange={(e) =>
                      setNuevoCofreNumero(e.target.value.replace(/[^\d]/g, ""))
                    }
                    inputMode="numeric"
                    placeholder="Ej: 1"
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                  />
                </label>
              </div>
            </div>

            <div className="mt-5 space-y-4 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-indigo-600" />
                <h4 className="text-sm font-semibold text-slate-800">
                  Titulares y autorizados
                </h4>
                <span className="text-xs text-slate-500">
                  (opcional · máx. {TITULAR_MAX} titulares y {AUTORIZADO_MAX} autorizados)
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    Titulares ({nuevosTitulares.length}/{TITULAR_MAX})
                  </p>
                  <button
                    type="button"
                    disabled={nuevosTitulares.length >= TITULAR_MAX}
                    onClick={() =>
                      setNuevosTitulares((prev) => [...prev, personaVacia("titular")])
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" /> Agregar titular
                  </button>
                </div>
                <div className="space-y-2">
                  {nuevosTitulares.map((p, i) => (
                    <CajaPersonaEditor
                      key={`titular-${i}`}
                      persona={p}
                      index={i}
                      onChange={(patch) =>
                        setNuevosTitulares((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, ...patch } : x))
                        )
                      }
                      onRemove={() =>
                        setNuevosTitulares((prev) => prev.filter((_, j) => j !== i))
                      }
                    />
                  ))}
                  {nuevosTitulares.length === 0 && (
                    <p className="text-xs text-slate-400">
                      Sin titulares adicionales.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Autorizados ({nuevosAutorizados.length}/{AUTORIZADO_MAX})
                  </p>
                  <button
                    type="button"
                    disabled={nuevosAutorizados.length >= AUTORIZADO_MAX}
                    onClick={() =>
                      setNuevosAutorizados((prev) => [
                        ...prev,
                        personaVacia("autorizado"),
                      ])
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    <Plus className="h-3.5 w-3.5" /> Agregar autorizado
                  </button>
                </div>
                <div className="space-y-2">
                  {nuevosAutorizados.map((p, i) => (
                    <CajaPersonaEditor
                      key={`autorizado-${i}`}
                      persona={p}
                      index={i}
                      onChange={(patch) =>
                        setNuevosAutorizados((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, ...patch } : x))
                        )
                      }
                      onRemove={() =>
                        setNuevosAutorizados((prev) =>
                          prev.filter((_, j) => j !== i)
                        )
                      }
                    />
                  ))}
                  {nuevosAutorizados.length === 0 && (
                    <p className="text-xs text-slate-400">Sin autorizados.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setMostrarCrearAsignar(false);
                  setNuevoSocioSeleccionado(null);
                  setNuevoCofreLetra("");
                  setNuevoCofreNumero("");
                  setNuevosTitulares([]);
                  setNuevosAutorizados([]);
                }}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                    if (!nuevoTamanoId || !nuevoSocioId.trim()) {
                      alert("Seleccioná tamaño y buscá y seleccioná un socio.");
                      return;
                    }
                    const personasIncompletas = [
                      ...nuevosTitulares,
                      ...nuevosAutorizados,
                    ].some((p) => !p.nombre.trim() || !p.cuit.trim());
                    if (personasIncompletas) {
                      alert(
                        "Completá Nombre y C.U.I.T. de cada titular/autorizado, o quitalos."
                      );
                      return;
                    }
                    try {
                      setLoading(true);
                      const token = await getAccessTokenSilently();
                      const creada = await crearCaja(
                        {
                          tamanoId: Number(nuevoTamanoId),
                          estado: "disponible",
                          ubicacion: nuevaUbicacion || undefined,
                          cofreLetra: nuevoCofreLetra || undefined,
                          cofreNumero: nuevoCofreNumero
                            ? Number(nuevoCofreNumero)
                            : undefined,
                          titulares: nuevosTitulares.map(personaPayload),
                          autorizados: nuevosAutorizados.map(personaPayload),
                        },
                        token
                      );
                    setCajas((prev) => {
                      const normalizada = {
                        ...creada,
                        id: (creada as any).id ?? (creada as any).caja_id,
                        tamanoId: Number(
                          (creada as any).tamanoId ?? (creada as any).tamano_id ?? 0
                        ),
                        personas: Array.isArray((creada as any).personas)
                          ? (creada as any).personas.map(normalizarPersona)
                          : [],
                      };
                      return [normalizada, ...prev];
                    });
                    const asignado: any = await asignarSocioACaja(
                      {
                        socioId: Number(nuevoSocioId),
                        cajaId: Number((creada as any).id ?? (creada as any).caja_id),
                      },
                      token
                    );
                    const asignadoNormalizado = normalizarSocioAsignado({
                      ...asignado,
                      socioId: Number(nuevoSocioId),
                      socioNombre: (asignado as any)?.socioNombre ?? nuevoSocioSeleccionado?.socio_nombre,
                      socio_dni: (asignado as any)?.socio_dni ?? nuevoSocioSeleccionado?.socio_dni,
                      socio_mail:
                        obtenerMailSocio(asignado) || obtenerMailSocio(nuevoSocioSeleccionado),
                    });
                    const cajaIdCreada = Number((creada as any).id ?? (creada as any).caja_id);
                    if (seleccionCaja?.id === cajaIdCreada) {
                      setSociosDeCaja((prev) => [...prev, asignadoNormalizado]);
                    }
                    setCajas((prev) =>
                      prev.map((c) => {
                        if (c.id !== cajaIdCreada) return c;
                        const sociosActualizados = Array.isArray(c.socios)
                          ? [...c.socios, asignadoNormalizado].filter(
                              (s, i, arr) =>
                                arr.findIndex((x) => String((x as any).socioId) === String((s as any).socioId)) === i
                            )
                          : [asignadoNormalizado];
                        return {
                          ...c,
                          socios: sociosActualizados,
                          titularNombre: asignadoNormalizado.socioNombre,
                        };
                      })
                    );
                    if (asignado?.warning) {
                      setMsg(`Caja creada y socio asignado. ${asignado.warning}`);
                      setMsgTipo("warn");
                    } else {
                      setMsg("Caja creada y socio asignado. El contrato único de la caja se genera al descargar o firmar.");
                      setMsgTipo("ok");
                    }
                    setMostrarCrearAsignar(false);
                    setNuevoTamanoId(undefined);
                    setNuevoSocioId("");
                    setNuevoSocioSeleccionado(null);
                    setNuevaUbicacion("");
                    setNuevoCofreLetra("");
                    setNuevoCofreNumero("");
                    setNuevosTitulares([]);
                    setNuevosAutorizados([]);
                  } catch (err: any) {
                    console.error("No se pudo crear/asignar", err);
                    const msg = err?.response?.data?.message || "No se pudo crear o asignar la caja.";
                    alert(msg);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                Crear y asignar
              </button>
            </div>
          </div>
        </div>
      )}

      {editarTamano && (
        <div
          className="fixed inset-0 z-[125] flex items-center justify-center bg-slate-900/20 px-4 py-8 backdrop-blur-md"
          style={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-400 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Editar tamaño
                </p>
                <h3 className="text-2xl font-semibold text-gray-900">
                  {editarTamano.nombre}
                </h3>
              </div>
              <button
                onClick={() => setEditarTamano(null)}
                className="rounded-lg px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
              >
                Cerrar
              </button>
            </div>

            <div className="mt-4 grid gap-3">
              <label className="text-sm text-gray-700">
                Nombre
                <input
                  value={editarTamanoForm.nombre}
                  onChange={(e) =>
                    setEditarTamanoForm((f) => ({ ...f, nombre: e.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                />
              </label>
              <label className="text-sm text-gray-700">
                Descripción
                <textarea
                  value={editarTamanoForm.descripcion}
                  onChange={(e) =>
                    setEditarTamanoForm((f) => ({ ...f, descripcion: e.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                  rows={3}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-gray-700">
                  Precio mensual
                  <input
                    type="number"
                    value={editarTamanoForm.precioMensual}
                    onChange={(e) =>
                      setEditarTamanoForm((f) => ({
                        ...f,
                        precioMensual: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                    placeholder="Ej: 40000"
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Precio anual
                  <input
                    type="number"
                    value={editarTamanoForm.precioAnual}
                    onChange={(e) =>
                      setEditarTamanoForm((f) => ({
                        ...f,
                        precioAnual: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-400 focus:outline-none"
                    placeholder="Ej: 400000"
                  />
                </label>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setEditarTamano(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                disabled={guardandoTamano}
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicionTamano}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70"
                disabled={guardandoTamano}
              >
                {guardandoTamano ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CajaDetalleDrawer: React.FC<{
  caja: Caja;
  socios: SocioCaja[];
  onClose: () => void;
  onDesasignar: (cajaId: number, socioId: number) => Promise<void> | void;
  onAsignar: (
    socioId: string | number,
    payload: Partial<SocioCaja>
  ) => Promise<SocioCaja | void> | SocioCaja | void;
  onEditarCaja: (caja: Caja) => Promise<void> | void;
}> = ({ caja, socios, onClose, onDesasignar, onAsignar, onEditarCaja }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgTipo, setMsgTipo] = useState<"ok" | "warn" | "error" | null>(null);
  const [confirmDesasignar, setConfirmDesasignar] = useState<SocioCaja | null>(null);
  const [desasignando, setDesasignando] = useState(false);
  const [editandoCodigo, setEditandoCodigo] = useState(false);
  const [editarLetra, setEditarLetra] = useState(
    String((caja as any)?.cofreLetra ?? codigoCajaDe(caja).split("-")[0] ?? "")
  );
  const [editarNumero, setEditarNumero] = useState(
    (caja as any)?.cofreNumero != null
      ? String((caja as any)?.cofreNumero)
      : String(codigoCajaDe(caja).match(/\d+/)?.[0] ?? "")
  );
  const [guardandoCodigo, setGuardandoCodigo] = useState(false);
  const [contratoCaja, setContratoCaja] = useState<{
    id?: number | string;
    firmaDigital?: string;
    firmado?: boolean;
  }>({});
  const [firmandoContrato, setFirmandoContrato] = useState(false);
  const [detalleSocios, setDetalleSocios] = useState<Record<string, any>>({});
  const [personas, setPersonas] = useState<CajaPersona[]>(
    Array.isArray(caja.personas) ? caja.personas.map(normalizarPersona) : []
  );
  const [formPersona, setFormPersona] = useState<CajaPersona | null>(null);
  const [guardandoPersona, setGuardandoPersona] = useState(false);
  const [personaMsg, setPersonaMsg] = useState<string | null>(null);
  const [personaMsgTipo, setPersonaMsgTipo] = useState<"ok" | "error" | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const isLegacyCaja =
    (caja as any)?.origen === "legacy" ||
    Boolean((caja as any)?.legacyContratoId) ||
    Number((caja as any)?.id) < 0;

  const titulares = personas.filter((p) => p.tipo === "titular");
  const autorizados = personas.filter((p) => p.tipo === "autorizado");

  const cargarPersonas = async () => {
    if (isLegacyCaja) return;
    try {
      const token = await getAccessTokenSilently();
      const data = await obtenerPersonasDeCaja(Number(caja.id), token);
      setPersonas(Array.isArray(data) ? data.map(normalizarPersona) : []);
    } catch (err) {
      console.error("No se pudieron cargar los titulares/autorizados", err);
    }
  };

  useEffect(() => {
    cargarPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caja.id]);

  const abrirNuevaPersona = (tipo: TipoPersonaCaja) => {
    setPersonaMsg(null);
    setPersonaMsgTipo(null);
    setFormPersona(personaVacia(tipo));
  };

  const guardarPersona = async () => {
    if (!formPersona) return;
    if (!formPersona.nombre.trim() || !formPersona.cuit.trim()) {
      setPersonaMsg("Nombre y C.U.I.T. son obligatorios.");
      setPersonaMsgTipo("error");
      return;
    }
    try {
      setGuardandoPersona(true);
      const token = await getAccessTokenSilently();
      if (formPersona.id) {
        const actualizada = await actualizarPersonaCaja(
          formPersona.id,
          personaPayload(formPersona),
          token
        );
        setPersonas((prev) =>
          prev.map((p) =>
            p.id === formPersona.id ? normalizarPersona(actualizada) : p
          )
        );
      } else {
        const creada = await crearPersonaCaja(
          Number(caja.id),
          personaPayload(formPersona),
          token
        );
        setPersonas((prev) => [...prev, normalizarPersona(creada)]);
      }
      setFormPersona(null);
      setPersonaMsg("Titular/autorizado guardado correctamente.");
      setPersonaMsgTipo("ok");
    } catch (err: any) {
      setPersonaMsg(
        err?.response?.data?.message || "No se pudo guardar el titular/autorizado."
      );
      setPersonaMsgTipo("error");
    } finally {
      setGuardandoPersona(false);
    }
  };

  const eliminarPersona = async (persona: CajaPersona) => {
    if (!persona.id) return;
    if (!window.confirm(`¿Quitar a ${persona.nombre || "esta persona"}?`)) return;
    try {
      const token = await getAccessTokenSilently();
      await eliminarPersonaCaja(persona.id, token);
      setPersonas((prev) => prev.filter((p) => p.id !== persona.id));
      setPersonaMsg("Se quitó correctamente.");
      setPersonaMsgTipo("ok");
    } catch (err: any) {
      setPersonaMsg(
        err?.response?.data?.message || "No se pudo quitar."
      );
      setPersonaMsgTipo("error");
    }
  };

  const keySocio = (socioId?: string | number, fallbackId?: number) =>
    String(socioId ?? fallbackId ?? "");

  useEffect(() => {
    let mounted = true;
    const cargarDetalles = async () => {
      try {
        const pendientes = socios.filter((s) => {
          const key = keySocio(s.socioId, s.id);
          return key && !detalleSocios[key];
        });
        if (!pendientes.length) return;
        const data = await Promise.all(
          pendientes.map(async (s) => {
            const sid = String(s.socioId ?? s.id);
            const socio = await socioService.getSocioById(sid, getAccessTokenSilently);
            return [sid, socio] as const;
          })
        );
        if (!mounted) return;
        setDetalleSocios((prev) => {
          const next = { ...prev };
          data.forEach(([sid, socio]) => {
            next[sid] = socio;
          });
          return next;
        });
      } catch (err) {
        console.error("No se pudieron cargar datos de socios para contratos", err);
      }
    };
    cargarDetalles();
    return () => {
      mounted = false;
    };
  }, [socios, getAccessTokenSilently, detalleSocios]);

  const sociosContratoKey = useMemo(
    () => socios.map((s) => `${s.socioId}-${s.esTitular ? "T" : "C"}`).join("|"),
    [socios]
  );

  useEffect(() => {
    setContratoCaja({});
  }, [sociosContratoKey]);

  const domicilioElectronicoSocio = (s: SocioCaja) => {
    const key = keySocio(s.socioId, s.id);
    const detalle = detalleSocios[key];
    return (
      s.socioMail ||
      obtenerMailSocio(detalle) ||
      "No informado"
    );
  };

  const firmantesContrato = socios.map((s) => ({
    socioId: Number(s.socioId ?? s.id),
    socioNombre: s.socioNombre,
    socioDni: s.socioDni,
    emailOficial: domicilioElectronicoSocio(s),
    esTitular: !!s.esTitular,
  }));

  const titularContrato =
    firmantesContrato.find((f) => f.esTitular) || firmantesContrato[0];
  const cotitularesContrato = firmantesContrato.filter(
    (f) => f.socioId !== titularContrato?.socioId
  );

  const asegurarContratoCaja = async (token: string) => {
    if (contratoCaja.id && !String(contratoCaja.id).startsWith("local-")) {
      return contratoCaja;
    }
    if (!titularContrato) return null;
    const generado = await ContratosService.crearContratoPorAsociacion(
      {
        socioId: titularContrato.socioId,
        cajaId: Number(caja.id),
        socioNombre: titularContrato.socioNombre,
        socioDni: titularContrato.socioDni,
        emailOficial: titularContrato.emailOficial,
        cajaNumero: codigoCajaDe(caja),
        socios: firmantesContrato,
      },
      token
    );
    setContratoCaja((prev) => ({
      ...prev,
      id: generado?.id || `local-caja-${caja.id}`,
    }));
    return generado;
  };

  const descargarContratoCaja = async () => {
    if (!firmantesContrato.length) {
      setMsg("No hay socios asociados para generar el contrato.");
      setMsgTipo("error");
      return;
    }
    const token = await getAccessTokenSilently();
    const contratoActual = contratoCaja.id
      ? contratoCaja
      : (await asegurarContratoCaja(token)) || {};
    const doc = contratoHtml({
      titular: titularContrato,
      cotitulares: cotitularesContrato,
      cajaNumero: codigoCajaDe(caja),
      tamanoNombre: caja.tamano?.nombre,
      firmaDigital: contratoActual?.firmaDigital,
    });
    descargarArchivo(doc, `contrato-caja-${codigoCajaDe(caja).replace(/[^\w-]+/g, "")}.html`);
    if (!contratoActual?.id) {
      setContratoCaja((prev) => ({
        ...prev,
        id: `local-caja-${caja.id}`,
      }));
    }
  };

  const firmarContratoCaja = async () => {
    const firmante = window.prompt("Nombre y apellido para firma digital");
    if (!firmante?.trim()) return;
    const baseFirma = `${firmante.trim()}|${caja.id}|${firmantesContrato
      .map((f) => f.socioId)
      .join(",")}|${new Date().toISOString()}`;
    const bytes = new TextEncoder().encode(baseFirma);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    const firmaDigital = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    try {
      if (!firmantesContrato.length || !titularContrato) {
        setMsg("No hay socios asociados para firmar el contrato.");
        setMsgTipo("error");
        return;
      }
      setFirmandoContrato(true);
      const token = await getAccessTokenSilently();
      const contrato = (await asegurarContratoCaja(token)) || {};
      const contratoId = contrato?.id || `local-caja-${caja.id}`;
      if (!String(contratoId).startsWith("local-")) {
        await ContratosService.firmarContratoDigital(
          contratoId,
          { firmante: firmante.trim(), firmaDigital },
          token
        );
      }
      setContratoCaja((prev) => ({
        ...prev,
        id: contratoId,
        firmaDigital,
        firmado: true,
      }));
      setMsg("Contrato de la caja firmado digitalmente.");
      setMsgTipo("ok");
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo firmar digitalmente.";
      setMsg(apiMsg);
      setMsgTipo("error");
    } finally {
      setFirmandoContrato(false);
    }
  };

  const buscar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    try {
      setBuscando(true);
      const data = await socioService.searchSocios(
        searchTerm.trim(),
        getAccessTokenSilently
      );
      setResultados(data);
    } catch (err) {
      console.error("No se pudo buscar socio", err);
    } finally {
      setBuscando(false);
    }
  };

  const solicitarDesasignar = (socio: SocioCaja) => {
    if (socios.length <= 1) {
      setMsg("No se puede desasignar porque la caja solo tiene un socio.");
      setMsgTipo("error");
      return;
    }
    setConfirmDesasignar(socio);
  };

  const confirmarDesasignar = async () => {
    if (!confirmDesasignar) return;
    try {
      setDesasignando(true);
      const socioId = confirmDesasignar.socioId ?? confirmDesasignar.id;
      if (!socioId) throw new Error("No se encontró el id del socio.");
      await onDesasignar(caja.id, Number(socioId));
      setMsg("Socio desasignado correctamente.");
      setMsgTipo("ok");
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo desasignar.";
      setMsg(apiMsg);
      setMsgTipo("error");
    } finally {
      setDesasignando(false);
      setConfirmDesasignar(null);
    }
  };

  const guardarCodigoCaja = async () => {
    const letra = resolverLetraCofre(editarLetra);
    const numero = Number(editarNumero);
    if (!letra || !editarNumero || !Number.isFinite(numero) || numero <= 0) {
      setMsg("Ingresá una letra válida (A-Z) y un número mayor a 0.");
      setMsgTipo("error");
      return;
    }
    try {
      setGuardandoCodigo(true);
      const token = await getAccessTokenSilently();
      const actualizada: any = await actualizarCaja(
        caja.id,
        { cofreLetra: letra, cofreNumero: numero },
        token
      );
      const codigo = `${letra}-${numero}`;
      await onEditarCaja({
        ...caja,
        ...(actualizada || {}),
        numero: actualizada?.numero ?? codigo,
        codigoCaja: codigo,
        cofreLetra: letra,
        cofreNumero: numero,
      } as Caja);
      setEditandoCodigo(false);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message || err?.message || "No se pudo actualizar el código de la caja.";
      setMsg(apiMsg);
      setMsgTipo("error");
    } finally {
      setGuardandoCodigo(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/20 px-4 py-8 backdrop-blur-md"
      style={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
    >
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto overscroll-contain rounded-2xl bg-white p-6 shadow-2xl shadow-slate-700/20">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                Caja {caja.id}
              </p>
              <h3 className="text-2xl font-semibold text-slate-900">
                Caja {codigoCajaDe(caja)}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Estado: <span className="font-semibold">{caja.estado || "—"}</span>
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Ubicación: <span className="font-semibold">{caja.ubicacion || "—"}</span>
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-1">
                  Tamaño: <span className="font-semibold">{caja.tamano?.nombre || "—"}</span>
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start">
            {!isLegacyCaja && (
              <button
                onClick={() => {
                  setEditarLetra(
                    String((caja as any)?.cofreLetra ?? codigoCajaDe(caja).split("-")[0] ?? codigoCajaDe(caja) ?? "")
                  );
                  setEditarNumero(
                    (caja as any)?.cofreNumero != null
                      ? String((caja as any)?.cofreNumero)
                      : String(codigoCajaDe(caja).match(/\d+/)?.[0] ?? "")
                  );
                  setEditandoCodigo(true);
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" />
                Editar código
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cerrar
            </button>
          </div>
        </div>

        {editandoCodigo && !isLegacyCaja && (
          <div className="mt-3 grid gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <label className="text-xs font-semibold text-indigo-900">
              Letra (módulo)
              <input
                value={editarLetra}
                onChange={(e) => setEditarLetra(e.target.value.toUpperCase())}
                list="letras-cofre"
                maxLength={2}
                placeholder="Ej: A"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-indigo-900">
              Número
              <input
                value={editarNumero}
                onChange={(e) => setEditarNumero(e.target.value.replace(/[^\d]/g, ""))}
                inputMode="numeric"
                placeholder="Ej: 1"
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
              />
            </label>
            <div className="flex gap-2">
              <button
                onClick={guardarCodigoCaja}
                disabled={guardandoCodigo}
                className="inline-flex h-10 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-70"
              >
                {guardandoCodigo ? "Guardando..." : "Guardar"}
              </button>
              <button
                onClick={() => setEditandoCodigo(false)}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {msg && (
          <div
            className={`mb-3 rounded-lg px-3 py-2 text-sm ${
              msgTipo === "error"
                ? "bg-red-50 text-red-700"
                : msgTipo === "warn"
                ? "bg-amber-50 text-amber-700"
                : "bg-emerald-50 text-emerald-700"
            }`}
          >
            {msg}
          </div>
        )}

        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.1fr]">
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">
                Socios asignados
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {socios.length} asociado(s)
                </span>
                {!isLegacyCaja && (
                  <>
                    <button
                      onClick={descargarContratoCaja}
                      className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Descargar contrato caja
                    </button>
                    <button
                      onClick={firmarContratoCaja}
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-70"
                      disabled={firmandoContrato}
                    >
                      <FileSignature className="h-3.5 w-3.5" />
                      {firmandoContrato ? "Firmando..." : "Firmar contrato caja"}
                    </button>
                  </>
                )}
              </div>
            </div>
            {contratoCaja?.firmado && (
              <p className="text-[11px] font-semibold text-emerald-700">
                Contrato único de la caja firmado digitalmente
              </p>
            )}
            <p className="text-[11px] text-slate-500">
              El contrato es único por caja e incluye titular y co-titulares.
            </p>
            <div className="space-y-2">
              {socios.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm"
                >
                  <div className="pr-3">
                    <p className="font-semibold text-slate-900">
                      {s.socioNombre || s.socioId || "Sin nombre"}
                    </p>
                    <p className="text-xs text-gray-600">
                      ID: {s.socioId || "—"} · DNI: {s.socioDni || "—"} · Rol:{" "}
                      {s.esTitular ? "Titular" : "Adicional"}
                    </p>
                    <DomicilioElectronicoBadge value={domicilioElectronicoSocio(s)} />
                    <p className="text-xs text-gray-500">
                      Desde: {s.fechaInicio || "—"} · Hasta: {s.fechaFin || "—"}
                    </p>
                    {s.nota && (
                      <p className="text-xs text-gray-500">Nota: {s.nota}</p>
                    )}
                  </div>
                  {!isLegacyCaja && (
                    <button
                      onClick={() => solicitarDesasignar(s)}
                      className="rounded-lg bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"
                    >
                      Desasignar
                    </button>
                  )}
                </div>
              ))}
              {socios.length === 0 && (
                <p className="text-xs text-gray-500">
                  Sin socios asignados todavía.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-inner">
            {isLegacyCaja ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Cofre legacy en modo lectura. Podés ver datos y socios, pero no asignar/desasignar desde este modal.
              </div>
            ) : (
              <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Asignar nuevo socio
                </p>
                <p className="text-xs text-slate-500">
                  Ingresá nombre, ID o DNI para buscar.
                </p>
              </div>
            </div>
            <form onSubmit={buscar} className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <label className="text-xs font-semibold text-slate-600">
                Búsqueda
                <input
                  type="text"
                  placeholder="Buscar por nombre, ID o DNI"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <button
                type="submit"
                className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70"
                disabled={!searchTerm.trim() || buscando}
              >
                {buscando ? "Buscando..." : "Buscar"}
              </button>
            </form>

            <div className="grid max-h-72 gap-2 overflow-y-auto">
              {resultados.map((s) => (
                <div
                  key={s.socio_id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-semibold">{s.socio_nombre}</p>
                    <p className="text-xs text-gray-500">
                      ID: {s.socio_id} · DNI: {s.socio_dni || "—"}
                    </p>
                    <DomicilioElectronicoBadge value={obtenerMailSocio(s) || "No informado"} />
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const asignado = await onAsignar(s.socio_id, {
                          socioNombre: s.socio_nombre,
                          socioDni: s.socio_dni,
                          socioMail: obtenerMailSocio(s),
                        });
                        if (asignado) {
                          setMsg("Socio asignado correctamente.");
                          setMsgTipo("ok");
                          setSearchTerm("");
                          setResultados([]);
                        }
                      } catch (err: any) {
                        const apiMsg =
                          err?.response?.data?.message || err?.message || "No se pudo asignar.";
                        setMsg(apiMsg);
                        setMsgTipo("error");
                      }
                    }}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
                  >
                    Asignar
                  </button>
                </div>
              ))}
              {resultados.length === 0 && (
                <p className="text-xs text-gray-500">
                  Buscá un socio para asignarlo a esta caja.
                </p>
              )}
            </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-inner">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-indigo-600" />
              <p className="text-sm font-semibold text-slate-800">
                Titulares y autorizados
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Máx. {TITULAR_MAX} titulares · {AUTORIZADO_MAX} autorizados · opcional
            </p>
          </div>

          {personaMsg && (
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                personaMsgTipo === "error"
                  ? "bg-red-50 text-red-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {personaMsg}
            </div>
          )}

          {isLegacyCaja ? (
            <p className="text-xs text-slate-500">
              El cofre legacy no admite titulares/autorizados.
            </p>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                {(["titular", "autorizado"] as TipoPersonaCaja[]).map((tipo) => {
                  const lista = tipo === "titular" ? titulares : autorizados;
                  const max = tipo === "titular" ? TITULAR_MAX : AUTORIZADO_MAX;
                  return (
                    <div
                      key={tipo}
                      className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <p
                          className={`text-xs font-semibold uppercase tracking-wide ${
                            tipo === "titular"
                              ? "text-indigo-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {tipo === "titular" ? "Titulares" : "Autorizados"} (
                          {lista.length}/{max})
                        </p>
                        <button
                          onClick={() => abrirNuevaPersona(tipo)}
                          disabled={lista.length >= max}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        >
                          <Plus className="h-3.5 w-3.5" /> Agregar
                        </button>
                      </div>
                      {lista.map((p) => (
                        <div
                          key={p.id ?? `${p.tipo}-${p.nombre}-${p.cuit}`}
                          className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">
                              {p.nombre || "Sin nombre"}
                            </p>
                            <p className="text-xs text-gray-600">
                              C.U.I.T.: {p.cuit || "—"}
                              {p.tipoDocumento
                                ? ` · ${p.tipoDocumento} ${p.numeroDocumento || ""}`
                                : ""}
                            </p>
                            {(p.ciudad || p.provincia) && (
                              <p className="text-xs text-gray-500">
                                {[p.calle, p.numero, p.ciudad, p.provincia]
                                  .filter(Boolean)
                                  .join(" ")}
                              </p>
                            )}
                            {p.idTarjeta && (
                              <p className="text-xs text-gray-500">
                                ID Tarjeta: {p.idTarjeta}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              onClick={() => {
                                setPersonaMsg(null);
                                setPersonaMsgTipo(null);
                                setFormPersona({ ...p });
                              }}
                              className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-600 hover:bg-slate-100"
                              aria-label="Editar"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => eliminarPersona(p)}
                              className="rounded-lg border border-red-100 bg-red-50 p-1.5 text-red-600 hover:bg-red-100"
                              aria-label="Quitar"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {lista.length === 0 && (
                        <p className="text-xs text-slate-400">
                          Sin {tipo === "titular" ? "titulares" : "autorizados"}.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {formPersona && (
                <div className="space-y-2">
                  <CajaPersonaEditor
                    persona={formPersona}
                    index={0}
                    onChange={(patch) =>
                      setFormPersona((prev) => (prev ? { ...prev, ...patch } : prev))
                    }
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setFormPersona(null)}
                      className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                      disabled={guardandoPersona}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={guardarPersona}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-70"
                      disabled={guardandoPersona}
                    >
                      {guardandoPersona ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {confirmDesasignar && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-2xl border border-red-200 bg-white p-5 shadow-2xl">
            <h4 className="text-lg font-semibold text-gray-900">
              Desasignar socio
            </h4>
            <p className="mt-2 text-sm text-gray-700">
              Vas a desasignar a{" "}
              <span className="font-semibold">
                {confirmDesasignar.socioNombre || confirmDesasignar.socioId}
              </span>{" "}
              de la caja {codigoCajaDe(caja)}. ¿Continuar?
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDesasignar(null)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                disabled={desasignando}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarDesasignar}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-70"
                disabled={desasignando}
              >
                {desasignando ? "Desasignando..." : "Desasignar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajasPage;
